import React, { useState, useContext, useEffect } from "react"
import CertHistoryContext from "context/certHistory/CertHistoryContext"
import { Helmet } from "react-helmet-async"
import { useParams, useNavigate } from "react-router-dom"
import { Container } from "reactstrap"
import Loading from "common/Loading"
import CertHistoryRow from "components/CertHistory/CertHistoryRow"
import DownloadBudget from "components/Feed/DownloadBudget"
import DownloadBudgetEnum from "common/DownloadBudgetEnum"
import ConfirmationModal from "common/ConfirmationModal"
import DialogCertMeasures from "components/CertHistory/DialogCertMediciones/DialogCertMeasures"
import InvoicingToolbar from "components/CertHistory/InvoicingToolbar"
import ModalInvoiceDraftConfirm from "components/CertHistory/ModalInvoiceDraftConfirm"
import i18n from "i18n"
import EntitlementPageGuard from "components/Subscription/EntitlementPageGuard"
import { CERTIFICATIONS } from "constants/entitlements"
import { Box, Typography } from "@mui/material"
import EmptyState from "common/EmptyState"
import useCertificationsHistoryInvoiceDraft from "./hooks/useCertificationsHistoryInvoiceDraft"
import { useSnackbar } from "notistack"

const CertificationsHistoryIndex = () => {
  const [openDialogMeasures, setOpenDialogMeasures] = useState(false)
  const [selectedPartMeasures, setSelectedPartMeasures] = useState(null)
  const [deletingCert, setDeletingCert] = useState(false)
  const { projectId } = useParams()
  const navigate = useNavigate()
  const { enqueueSnackbar } = useSnackbar()
  const {
    setCertHistory,
    certHistoryTree,
    loading,
    exportSideBar,
    openConfirmDelete,
    setOpenConfirmDelete,
    setCetToDelete,
    deleteCert,
    certToDelete,
  } = useContext(CertHistoryContext)
  const {
    isInvoicing,
    handleToggleInvoicing,
    handleClearSelectedParts,
    isInvoiceDraftConfirmOpen,
    handleOpenInvoiceDraftConfirm,
    handleCloseInvoiceDraftConfirm,
    handleAcceptInvoiceDraft,
    selectedParts,
    invoicingDrafts,
    handleToggleSelectedPart,
    handleToggleSelectedChapter,
    handleUpdateInvoicingDraft,
    handlePersistInvoicingAmount,
    handleApplyPercentToSelected,
    selectedPartsCount,
    selectedInvoicingTotal,
  } = useCertificationsHistoryInvoiceDraft({
    certHistoryTree,
    projectId,
    onInvoiceCreated: () => setCertHistory(projectId),
  })

  useEffect(() => {
    setCertHistory(projectId)
  }, [projectId])

  if (loading) {
    return (
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          width: "100%",
          minHeight: "calc(100dvh - 160px)",
        }}
      >
        <Loading />
      </Box>
    )
  }

  const handleClose = () => {
    setCetToDelete(null)
    setOpenConfirmDelete(false)
  }

  const handleDeleteCert = async () => {
    setDeletingCert(true)
    const result = await deleteCert(projectId, certToDelete)
    setDeletingCert(false)
    if (result && !result.success) {
      const msgKey = `certHistory.deleteError.${result.code}`
      const fallbackKey = "certHistory.deleteError.default"
      const msg = i18n.exists(msgKey) ? i18n.t(msgKey) : i18n.t(fallbackKey)
      enqueueSnackbar(msg, { variant: "error" })
    }
  }

  return (
    <EntitlementPageGuard permission={CERTIFICATIONS}>
      <div className="page-content">
        <Helmet>
          <title>Planhopper | Proyecto </title>
        </Helmet>
        <Container fluid>
          <Box
            sx={{
              display: "flex",
              alignItems: "flex-start",
              justifyContent: "space-between",
              gap: 2,
              mb: 3,
              flexWrap: "wrap",
            }}
          >
            <Typography variant="h6" component="h1" sx={{ mb: 0 }}>
              Histórico de certificaciones
            </Typography>
            <InvoicingToolbar
              isInvoicing={isInvoicing}
              onToggle={handleToggleInvoicing}
              onClearSelection={handleClearSelectedParts}
              onCreateInvoice={handleOpenInvoiceDraftConfirm}
              onApplyPercent={handleApplyPercentToSelected}
              selectedPartsCount={selectedPartsCount}
              totalInvoicing={selectedInvoicingTotal}
              disabled={
                !certHistoryTree.certs || certHistoryTree.certs.length === 0
              }
            />
          </Box>
          {certHistoryTree.certs && certHistoryTree.certs.length > 0 ? (
            certHistoryTree.certs.map((c, index) => (
              <CertHistoryRow
                key={c.id}
                data={c}
                title={`CERTIFICACIÓN ${index + 1}`}
                lastCert={index === certHistoryTree.certs.length - 1}
                isInvoicing={isInvoicing}
                selectedParts={selectedParts}
                invoicingDrafts={invoicingDrafts}
                onToggleSelectedPart={handleToggleSelectedPart}
                onToggleSelectedChapter={handleToggleSelectedChapter}
                onUpdateInvoicingDraft={handleUpdateInvoicingDraft}
                onPersistInvoicingAmount={handlePersistInvoicingAmount}
                onOpenMeasuresDialog={part => {
                  setSelectedPartMeasures(part)
                  setOpenDialogMeasures(true)
                }}
              />
            ))
          ) : (
            <EmptyState
              title={i18n.t("certHistory.noCertificationsYet")}
              buttonText={i18n.t("certHistory.certify")}
              onButtonClick={() => navigate(`/certificaciones/${projectId}`)}
            />
          )}
        </Container>
        <DownloadBudget
          open={exportSideBar.isOpen}
          setOpen={exportSideBar.setOpen}
          name="Certification"
          fromTab={DownloadBudgetEnum.FROM_HISTORY_CERTIFICATION}
        />

        <ConfirmationModal
          open={openConfirmDelete}
          onClose={handleClose}
          onConfirm={handleDeleteCert}
          title={i18n.t("certHistory.confirmationModal.title")}
          message={i18n.t("certHistory.confirmationModal.message")}
          confirmButtonText={i18n.t("buttons.delete")}
          confirmButtonColor="error"
          cancelButtonText={i18n.t("buttons.cancel")}
          confirmLoading={deletingCert}
        />
        <DialogCertMeasures
          open={openDialogMeasures}
          onClose={() => setOpenDialogMeasures(false)}
          part={selectedPartMeasures}
        />
        <ModalInvoiceDraftConfirm
          open={isInvoiceDraftConfirmOpen}
          onClose={handleCloseInvoiceDraftConfirm}
          onAccept={handleAcceptInvoiceDraft}
          totalInvoicing={selectedInvoicingTotal}
          selectedPartsCount={selectedPartsCount}
        />
      </div>
    </EntitlementPageGuard>
  )
}

export default CertificationsHistoryIndex
